import React, { useRef, useState, useEffect, useCallback } from 'react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Điện trở & định luật Ohm (Lớp 11 — Bài 16): 3 đồ thị
//  (a) I–U của điện trở không đổi: I = U/R (đường thẳng qua gốc).
//  (b) R–T đèn sợi đốt (kim loại): R tăng theo nhiệt độ — R = R₀(1 + α·T).
//  (c) R–T của NTC (nhiệt điện trở): R giảm mạnh khi nhiệt độ tăng.
const W = 690, H = 270, PTOP = 26, PBOT = H - 44

const ResistanceOhmSimulation: React.FC<Props> = ({
  title = 'Điện trở & định luật Ohm',
  description = 'Ba đồ thị: I–U của điện trở không đổi (đường thẳng), I–U của đèn sợi đốt (đường cong) và R–T của NTC (giảm).',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [U, setU] = useState(6), [R, setR] = useState(3), [T, setT] = useState(60)
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  // mô hình
  // Đèn sợi đốt: đặc tuyến I–U là ĐƯỜNG CONG lõm (R tăng khi nóng → I tăng chậm dần), I ∝ U^0.55
  const bulbIU = (u: number) => 0.12 * Math.pow(Math.max(0, u), 0.55)
  const ntcR = (t: number) => 10 * Math.exp(3000 * (1 / (t + 273) - 1 / 298))   // Ω, NTC (giảm)

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const panel = (px0: number, pw: number, o: { title: string; xl: string; yl: string; xmax: number; ymax: number; color: string; f: (x: number) => number; mx: number; xunit?: string }) => {
      const x0 = px0 + 36, x1 = px0 + pw - 12, y0 = PBOT, yTop = PTOP
      const X = (x: number) => x0 + (x / o.xmax) * (x1 - x0)
      const Y = (y: number) => y0 - (Math.min(y, o.ymax) / o.ymax) * (y0 - yTop)
      // lưới
      ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
      for (let i = 1; i <= 4; i++) { const gy = y0 - (i / 4) * (y0 - yTop); ctx.beginPath(); ctx.moveTo(x0, gy); ctx.lineTo(x1, gy); ctx.stroke() }
      for (let i = 1; i <= 4; i++) { const gx = x0 + (i / 4) * (x1 - x0); ctx.beginPath(); ctx.moveTo(gx, yTop); ctx.lineTo(gx, y0); ctx.stroke() }
      // trục + mũi tên
      ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 1.8; ctx.lineJoin = 'round'
      ctx.beginPath(); ctx.moveTo(x0, yTop - 2); ctx.lineTo(x0, y0); ctx.lineTo(x1, y0); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(x0, yTop - 8); ctx.lineTo(x0 - 4, yTop); ctx.lineTo(x0 + 4, yTop); ctx.closePath(); ctx.fill()
      ctx.beginPath(); ctx.moveTo(x1 + 6, y0); ctx.lineTo(x1 - 2, y0 - 4); ctx.lineTo(x1 - 2, y0 + 4); ctx.closePath(); ctx.fill()
      // nhãn
      ctx.font = 'bold 11px sans-serif'; ctx.fillStyle = '#334155'; ctx.textAlign = 'center'; ctx.fillText(o.title, (x0 + x1) / 2, 14)
      ctx.font = '10px sans-serif'; ctx.fillStyle = '#64748b'
      ctx.fillText(o.xl, (x0 + x1) / 2, y0 + 22); ctx.save(); ctx.translate(px0 + 10, (yTop + y0) / 2); ctx.rotate(-Math.PI / 2); ctx.fillText(o.yl, 0, 0); ctx.restore(); ctx.textAlign = 'left'
      // đường cong
      ctx.strokeStyle = o.color; ctx.lineWidth = 2.6; ctx.beginPath()
      for (let i = 0; i <= 80; i++) { const x = (i / 80) * o.xmax; const px = X(x), py = Y(o.f(x)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
      ctx.stroke()
      // điểm làm việc + gióng nét đứt
      const my = o.f(o.mx)
      ctx.strokeStyle = 'rgba(100,116,139,0.5)'; ctx.lineWidth = 1; ctx.setLineDash([3, 3])
      ctx.beginPath(); ctx.moveTo(X(o.mx), y0); ctx.lineTo(X(o.mx), Y(my)); ctx.lineTo(x0, Y(my)); ctx.stroke(); ctx.setLineDash([])
      ctx.fillStyle = o.color; ctx.beginPath(); ctx.arc(X(o.mx), Y(my), 4.5, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#0f172a'; ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`${my.toFixed(my < 1 ? 2 : my < 10 ? 1 : 0)}${o.xunit || ''}`, X(o.mx), Y(my) - 7); ctx.textAlign = 'left'
    }
    const pw = W / 3
    panel(0, pw, { title: 'I–U điện trở (R không đổi)', xl: 'U (V)', yl: 'I (A)', xmax: 12, ymax: 6, color: '#2563eb', f: (u) => u / R, mx: U })
    panel(pw, pw, { title: 'I–U đèn sợi đốt', xl: 'U (V)', yl: 'I (A)', xmax: 12, ymax: 0.5, color: '#ea580c', f: bulbIU, mx: U })
    panel(2 * pw, pw, { title: 'R–T của NTC', xl: 'T (°C)', yl: 'R (Ω)', xmax: 200, ymax: 26, color: '#7c3aed', f: ntcR, mx: T })
  }, [U, R, T])
  useEffect(() => { draw() }, [draw])

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-20 text-blue-700">U (V)</span><input type="range" min={0} max={12} step={0.5} value={U} onChange={(e) => { touch(); setU(Number(e.target.value)) }} className="flex-1 accent-blue-500" /><span className="w-12 text-right font-semibold">{U} V</span></label>
        <label className="flex items-center gap-2"><span className="w-20 text-blue-700">R (Ω)</span><input type="range" min={2} max={6} step={0.5} value={R} onChange={(e) => { touch(); setR(Number(e.target.value)) }} className="flex-1 accent-blue-500" /><span className="w-12 text-right font-semibold">{R} Ω</span></label>
        <label className="flex items-center gap-2"><span className="w-20 text-violet-700">T (°C)</span><input type="range" min={0} max={200} step={5} value={T} onChange={(e) => { touch(); setT(Number(e.target.value)) }} className="flex-1 accent-violet-500" /><span className="w-12 text-right font-semibold">{T}°C</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Điện trở không đổi: I tỉ lệ thuận U (đường thẳng qua gốc, độ dốc = 1/R). <b>Đèn sợi đốt</b>: đặc tuyến I–U là <b>đường cong lõm</b> — khi U tăng dây tóc nóng lên, R tăng nên I tăng chậm dần (U,I điều khiển bằng thanh U). NTC: R giảm mạnh khi nhiệt độ T tăng.</p>
    </div>
  )
}
export default ResistanceOhmSimulation
